import { Area } from 'react-easy-crop/types';
import { isMobile } from './deviceService';

const maxWidth = 600;
const maxHeigth = 800;

const isBase64 = (url: string): boolean => {
  if (!url || url === '' || url.trim() === '') {
    return false;
  }
  try {
    atob(url);
    return true;
  } catch (err) {
    return false;
  }
};

// eslint-disable-next-line compat/compat
const convertBlobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.readAsDataURL(blob);
});

const fetchBlobImage = (url: string): Promise<string> => fetch(url, {
  method: 'GET',
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
})
  .then((response: Response) => response.blob())
  .then((blob: Blob) => convertBlobToBase64(blob))
  .catch((err: Error) => {
    throw err;
  });

const createImageFromBase64 = (
  base64: string,
  resolve: (value?: HTMLImageElement | PromiseLike<HTMLImageElement>) => void,
  reject: (reason?: ErrorEvent) => void,
) => {
  const image = new Image();
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', (error) => reject(error));
  image.src = base64;
};

// eslint-disable-next-line compat/compat
const createImage = (url: string): Promise<HTMLImageElement> => new Promise(((resolve, reject) => {
  if (isBase64(url)) {
    createImageFromBase64(url, resolve, reject);
  } else {
    fetchBlobImage(url)
      .then((base64: string) => {
        createImageFromBase64(base64, resolve, reject);
      })
      .catch((err: Error) => {
        reject(err);
      });
  }
}));

// Mobile browser limit the max size of canvas
const getMaxMobileSize = (size: number): number =>
  Math.min(size, isMobile ? 4096 : 100000);

export const createCroppedImage = (
  picture: string,
  croppedAreaPixels: Area,
  orientation: number,
): Promise<string> => createImage(picture)
  .then((image: HTMLImageElement) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Create a fit-content safe frame
    const maxSize = Math.max(image.width, image.height);
    const safeArea = getMaxMobileSize(2 * ((maxSize / 2) * Math.sqrt(2)));
    canvas.width = safeArea;
    canvas.height = safeArea;

    // Rotate the image if needed
    ctx.translate(canvas.width / 2, canvas.height / 2);

    // Draw the image into the frame
    ctx.drawImage(image, -getMaxMobileSize(image.width) / 2, -getMaxMobileSize(image.height) / 2);

    // Focus to the cropped area inside the safe frame and keep the image ratio
    const data = ctx.getImageData(0, 0, safeArea, safeArea);
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    ctx.putImageData(
      data,
      0
      - safeArea / 2
      + (getMaxMobileSize(image.width)) / 2
      - croppedAreaPixels.x,
      0
      - safeArea / 2
      + (getMaxMobileSize(image.height)) / 2
      - croppedAreaPixels.y,
    );
    if (croppedAreaPixels.width < maxWidth) {
      return canvas.toDataURL('image/jpg', 0.8);
    }
    const oc = document.createElement('canvas');
    const octx = oc.getContext('2d');
    oc.width = maxWidth;
    oc.height = maxHeigth;
    octx.drawImage(canvas, 0, 0, maxWidth, maxHeigth);
    return oc.toDataURL('image/jpg', 0.8);
  }).catch((err: Error) => {
    throw err;
  });

// Source :
// https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side
export const getOrientation = (
  result: ArrayBuffer,
  callback: (orientation: number) => void,
): void => {
  const view = new DataView(result);
  if (view.getUint16(0, false) !== 0xFFD8) {
    return callback(-2);
  }
  const length = view.byteLength;
  let offset = 2;
  while (offset < length) {
    if (view.getUint16(offset + 2, false) <= 8) return callback(-1);
    const marker = view.getUint16(offset, false);
    offset += 2;
    if (marker === 0xFFE1) {
      offset += 2;
      if (view.getUint32(offset, false) !== 0x45786966) {
        return callback(-1);
      }

      const little = view.getUint16(offset += 6, false) === 0x4949;
      offset += view.getUint32(offset + 4, little);
      const tags = view.getUint16(offset, little);
      offset += 2;
      for (let i = 0; i < tags; i += 1) {
        if (view.getUint16(offset + (i * 12), little) === 0x0112) {
          return callback(view.getUint16(offset + (i * 12) + 8, little));
        }
      }
    } else if ((marker && 0xFF00) !== 0xFF00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }
  return callback(-1);
};
